/**
 * Stage 12 (A4): single-surface audio focus arbiter.
 *
 * Three surfaces play audio: `tts` (chat per-turn TTS via useAudioPlayer),
 * `music` (MusicGallery track play), `speech` (SpeechGallery inline audio
 * controls). Without arbitration they overlap. This module is a module-
 * level EventTarget: whichever surface calls `claimMediaFocus(x)` becomes
 * the current owner; every other subscriber receives a `claim` event and
 * pauses its own playback.
 *
 * Claiming your own surface is a no-op (idempotent). Muting a surface does
 * NOT claim focus — only actual playback does.
 */

export type MediaSurface = 'tts' | 'music' | 'speech';

interface ClaimDetail {
  prev: MediaSurface | null;
  owner: MediaSurface;
}

const target = new EventTarget();
let currentOwner: MediaSurface | null = null;

export function claimMediaFocus(owner: MediaSurface): void {
  if (currentOwner === owner) return;
  const prev = currentOwner;
  currentOwner = owner;
  target.dispatchEvent(
    new CustomEvent<ClaimDetail>('claim', { detail: { prev, owner } }),
  );
}

export function releaseMediaFocus(owner: MediaSurface): void {
  if (currentOwner === owner) currentOwner = null;
}

export function currentMediaOwner(): MediaSurface | null {
  return currentOwner;
}

export function onMediaFocusClaim(
  cb: (detail: ClaimDetail) => void,
): () => void {
  const handler = (e: Event): void => {
    cb((e as CustomEvent<ClaimDetail>).detail);
  };
  target.addEventListener('claim', handler);
  return () => target.removeEventListener('claim', handler);
}
